import React, { useState, useEffect } from 'react';
import { api } from '../services/api.ts';
import { normalizeDate, formatDueDate } from '../utils/dateUtils.ts';
import './Tasks.css';

interface Task {
  id: number;
  title: string;
  description: string;
  due_date: string;
  is_urgent: boolean;
  is_important: boolean;
  status: 'pending' | 'completed';
  transaction_id?: number;
  transaction?: {
    id: number;
    description: string;
    amount: number;
    type: 'income' | 'expense';
  };
  created_at: string;
  updated_at?: string;
}

interface Transaction {
  id: number;
  description: string;
  amount: number;
  type: 'income' | 'expense';
}

const Tasks: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<number>(1);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: new Date().toISOString().split('T')[0],
    is_urgent: false,
    is_important: false,
    transaction_id: undefined as number | undefined,
    status: 'pending' as 'pending' | 'completed',
    duplicate_occurrences: 0
  });
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    fetchTasks();
    fetchTransactions();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await api.get('/api/v1/tasks');
      setTasks(response.data);
    } catch (error) {
      console.error('Erro ao buscar tarefas:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await api.get('/api/v1/transactions');
      setTransactions(response.data);
    } catch (error) {
      console.error('Erro ao buscar transações:', error);
    }
  };

  const getTasksByQuadrant = (quadrant: number): Task[] => {
    return tasks.filter(task => {
      if (!showCompleted && task.status === 'completed') {
        return false;
      }
      const isUrgent = task.is_urgent;
      const isImportant = task.is_important;
      if (quadrant === 1) return isUrgent && isImportant;
      if (quadrant === 2) return !isUrgent && isImportant;
      if (quadrant === 3) return isUrgent && !isImportant;
      if (quadrant === 4) return !isUrgent && !isImportant;
      return false;
    });
  };

  const getNextMonthDate = (baseDate: string, monthsToAdd: number): string => {
    const date = new Date(baseDate);
    const originalDay = date.getDate();
    const targetYear = date.getFullYear() + Math.floor((date.getMonth() + monthsToAdd) / 12);
    const finalMonth = (date.getMonth() + monthsToAdd) % 12;
    
    // Criar nova data no mês/ano alvo
    const newDate = new Date(targetYear, finalMonth, 1);
    
    // Verificar quantos dias o mês alvo tem
    const lastDayOfMonth = new Date(targetYear, finalMonth + 1, 0).getDate();
    
    // Usar o dia original se existir no mês, senão usar o último dia do mês
    const dayToUse = Math.min(originalDay, lastDayOfMonth);
    newDate.setDate(dayToUse);
    
    return newDate.toISOString().split('T')[0];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const basePayload = {
        title: formData.title,
        description: formData.description,
        is_urgent: formData.is_urgent,
        is_important: formData.is_important,
        transaction_id: formData.transaction_id || null,
        status: formData.status
      };

      if (editingTask) {
        // Edição: apenas atualizar a tarefa existente
        const payload = {
          ...basePayload,
          due_date: formData.due_date
        };
        await api.put(`/api/v1/tasks/${editingTask.id}`, payload);
      } else {
        // Criação: criar tarefa(s) baseada(s) no número de ocorrências
        const occurrences = formData.duplicate_occurrences > 0 ? formData.duplicate_occurrences + 1 : 1;
        
        const promises = [];
        for (let i = 0; i < occurrences; i++) {
          const dueDate = getNextMonthDate(formData.due_date, i);
          const payload = {
            ...basePayload,
            due_date: dueDate
          };
          promises.push(api.post('/api/v1/tasks', payload));
        }
        
        await Promise.all(promises);
      }
      
      resetForm();
      fetchTasks();
    } catch (error) {
      console.error('Erro ao salvar tarefa:', error);
      alert('Erro ao salvar tarefa(s). Por favor, tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      due_date: new Date().toISOString().split('T')[0],
      is_urgent: false,
      is_important: false,
      transaction_id: undefined,
      status: 'pending',
      duplicate_occurrences: 0
    });
    setShowForm(false);
    setEditingTask(null);
  };

  const handleEdit = (task: Task) => {
    setEditingTask(task);
    // Normalizar a data para o formato YYYY-MM-DD (necessário para input type="date")
    const normalizedDate = normalizeDate(task.due_date);
    
    setFormData({
      title: task.title,
      description: task.description,
      due_date: normalizedDate,
      is_urgent: task.is_urgent,
      is_important: task.is_important,
      transaction_id: task.transaction_id,
      status: task.status,
      duplicate_occurrences: 0
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta tarefa?')) {
      setDeletingId(id);
      try {
        await api.delete(`/api/v1/tasks/${id}`);
        fetchTasks();
      } catch (error) {
        console.error('Erro ao excluir tarefa:', error);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleToggleStatus = async (task: Task) => {
    setTogglingId(task.id);
    try {
      if (task.status === 'pending') {
        await api.put(`/api/v1/tasks/${task.id}/complete`);
      } else {
        await api.put(`/api/v1/tasks/${task.id}/uncomplete`);
      }
      fetchTasks();
    } catch (error) {
      console.error('Erro ao alterar status da tarefa:', error);
    } finally {
      setTogglingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    // Garantir que a data de vencimento não sofra ajuste de fuso horário
    return formatDueDate(dateString);
  };

  const getDaysUntilDue = (dueDate: string): number => {
    // Normalizar data para evitar problemas de timezone
    const normalizedDueDate = normalizeDate(dueDate);
    const [year, month, day] = normalizedDueDate.split('-');
    const due = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    const diff = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getUrgencyClass = (dueDate: string): string => {
    const days = getDaysUntilDue(dueDate);
    if (days < 0) return 'overdue';
    if (days <= 1) return 'urgent';
    if (days <= 3) return 'warning';
    return 'normal';
  };

  const quadrantTabs = [
    { id: 1, label: 'Fazer Primeiro', description: 'Urgente e Importante', color: '#ef4444' },
    { id: 2, label: 'Agendar', description: 'Não Urgente mas Importante', color: '#f59e0b' },
    { id: 3, label: 'Delegar', description: 'Urgente mas Não Importante', color: '#3b82f6' },
    { id: 4, label: 'Eliminar', description: 'Não Urgente e Não Importante', color: '#6b7280' }
  ];

  if (loading) {
    return (
      <div className="tasks">
        <div className="page-header">
          <h1>📋 Tarefas</h1>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  const tasksByQuadrant = getTasksByQuadrant(activeTab);

  return (
    <div className="tasks">
      <div className="page-header">
        <h1>📋 Tarefas</h1>
        <p>Gerencie suas tarefas usando a Matriz de Eisenhower</p>
        <button
          className="primary-button"
          onClick={() => {
            resetForm();
            setShowForm(!showForm);
          }}
        >
          {showForm ? 'Cancelar' : 'Nova Tarefa'}
        </button>
      </div>

      {showForm && (
        <div className="form-container">
          <form onSubmit={handleSubmit} className="task-form">
            <h3>{editingTask ? 'Editar Tarefa' : 'Nova Tarefa'}</h3>
            
            <div className="form-group">
              <label htmlFor="title">Título *</label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="Digite o título da tarefa"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição opcional da tarefa"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="due_date">Data de Vencimento *</label>
              <input
                type="date"
                id="due_date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                required
              />
            </div>

            {!editingTask && (
              <div className="form-group">
                <label htmlFor="duplicate_occurrences">Duplicar nos próximos meses (opcional)</label>
                <input
                  type="number"
                  id="duplicate_occurrences"
                  min="0"
                  max="12"
                  value={formData.duplicate_occurrences}
                  onChange={(e) => setFormData({ ...formData, duplicate_occurrences: parseInt(e.target.value) || 0 })}
                  placeholder="Número de meses adicionais"
                />
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  {formData.duplicate_occurrences > 0 
                    ? `Serão criadas ${formData.duplicate_occurrences + 1} tarefa(s) com vencimento no mesmo dia de cada mês`
                    : 'Deixe em 0 para criar apenas uma tarefa'}
                </small>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <div className="toggle-container">
                  <label htmlFor="is_urgent" className="toggle-label">
                    Urgente
                  </label>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      id="is_urgent"
                      checked={formData.is_urgent}
                      onChange={(e) => setFormData({ ...formData, is_urgent: e.target.checked })}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
              <div className="form-group">
                <div className="toggle-container">
                  <label htmlFor="is_important" className="toggle-label">
                    Importante
                  </label>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      id="is_important"
                      checked={formData.is_important}
                      onChange={(e) => setFormData({ ...formData, is_important: e.target.checked })}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="transaction_id">Vincular Transação (opcional)</label>
              <select
                id="transaction_id"
                value={formData.transaction_id || ''}
                onChange={(e) => setFormData({ ...formData, transaction_id: e.target.value ? Number(e.target.value) : undefined })}
              >
                <option value="">Nenhuma</option>
                {transactions.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.description} - {t.type === 'income' ? '+' : '-'}R$ {Math.abs(t.amount).toFixed(2)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'pending' | 'completed' })}
              >
                <option value="pending">Pendente</option>
                <option value="completed">Concluída</option>
              </select>
            </div>

            <div className="form-actions">
              <button type="submit" className="primary-button" disabled={saving}>
                {saving ? 'Salvando...' : (editingTask ? 'Salvar Alterações' : 'Criar Tarefa')}
              </button>
              <button 
                type="button" 
                className="secondary-button"
                onClick={resetForm}
                disabled={saving}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="tasks-content">
        <div className="tasks-filters">
          <div className="toggle-container">
            <label htmlFor="show_completed" className="toggle-label">
              Mostrar concluídas
            </label>
            <label className="toggle-switch">
              <input
                type="checkbox"
                id="show_completed"
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
        <div className="segmented-control">
          {quadrantTabs.map(tab => (
            <button
              key={tab.id}
              className={`segment-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              style={{ 
                '--segment-color': tab.color 
              } as React.CSSProperties}
            >
              <span className="segment-label">{tab.label}</span>
              <span className="segment-description">{tab.description}</span>
            </button>
          ))}
        </div>

        <div className="tasks-list">
          {tasksByQuadrant.length === 0 ? (
            <div className="empty-state">
              <p>Nenhuma tarefa neste quadrante.</p>
            </div>
          ) : (
            tasksByQuadrant.map(task => {
              const urgencyClass = getUrgencyClass(task.due_date);
              const daysUntilDue = getDaysUntilDue(task.due_date);
              
              return (
                <div key={task.id} className={`task-card ${task.status === 'completed' ? 'completed' : ''}`}>
                  <div className="task-content-wrapper">
                    <div className="task-content">
                      <div className="task-header">
                        <h3>{task.title}</h3>
                      </div>
                      
                      {task.description && (
                        <p className="task-description">{task.description}</p>
                      )}
                      
                      <div className="task-meta">
                        <div className="task-due-date">
                          <span className="meta-label">Vencimento:</span>
                          <span className={`urgency-label ${urgencyClass}`}>
                            {formatDate(task.due_date)}
                            {daysUntilDue < 0 && ' (Vencida)'}
                            {daysUntilDue === 0 && ' (Hoje)'}
                            {daysUntilDue === 1 && ' (Amanhã)'}
                            {daysUntilDue > 1 && daysUntilDue <= 3 && ` (${daysUntilDue} dias)`}
                          </span>
                        </div>
                        
                        {task.transaction && (
                          <div className="task-transaction">
                            <span className="meta-label">Transação:</span>
                            <span className="transaction-link">
                              💰 {task.transaction.description}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="task-actions">
                      <div className="task-status-badge">
                        {task.status === 'completed' ? '✅ Concluída' : '⏳ Pendente'}
                      </div>
                      <button
                        onClick={() => handleToggleStatus(task)}
                        className="action-icon-button"
                        title={task.status === 'pending' ? 'Marcar como concluída' : 'Marcar como pendente'}
                        disabled={togglingId === task.id}
                      >
                        {togglingId === task.id ? '...' : (task.status === 'pending' ? '✅' : '↩️')}
                      </button>
                      <button
                        onClick={() => handleEdit(task)}
                        className="action-icon-button"
                        title="Editar tarefa"
                      >
                        <img src="/edit.svg" alt="Editar" className="icon" />
                      </button>
                      <button
                        onClick={() => handleDelete(task.id)}
                        className="action-icon-button delete"
                        title="Excluir tarefa"
                        disabled={deletingId === task.id}
                      >
                        {deletingId === task.id ? '...' : <img src="/delete.svg" alt="Excluir" className="icon" />}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Tasks;

